import { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Loading, Modal } from '@/shared/ui';
import WarningRed from '@/assets/icons/warning-red.svg';
import _ from 'lodash';
import { IUnserviceableInsurance } from '../../constants';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { CustomersController } from '@/shared/endpoints/customers';
import saleForceWebHookInstance from '@/services/saleforce';
import { selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { ROUTES } from '@/shared/constants/routes';
import { useNavigate } from 'react-router-dom';

interface IProps {
    conflict?: IUnserviceableInsurance | null;
    onAdd?(): void;
    onClose?(): void;
    onContinue?(): void;
    hasInsurance?: boolean;
    customDescriptionWebhookSuccess?: string;
    customDescriptionWebhookFailure?: string;
}

const InsuranceUnserviceableModal = ({
    conflict,
    onClose,
    onAdd,
    onContinue,
    hasInsurance,
    customDescriptionWebhookFailure,
    customDescriptionWebhookSuccess
}: IProps) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectOnboardingUser);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const exitAccount = () => dispatch(CustomersController.logout());

    const sendEmail = useCallback(async () => {
        if (!conflict || !user) return;

        setLoading(true);
        setHasError(false);

        try {
            await saleForceWebHookInstance.post({ email: user.email, phone: '', activation: conflict.activation }, conflict?.resource);
        } catch {
            setHasError(true);
        }
        navigate(`/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.INSURANCE.MAIN}`);

        setSuccess(true);
        setLoading(false);
    }, [conflict]);

    useEffect(() => {
        sendEmail();
    }, [sendEmail]);

    const description = useMemo(() => {
        if (hasError) {
            if (customDescriptionWebhookFailure) {
                return customDescriptionWebhookFailure;
            }
            return 'We have reviewed your insurance details and found that your current insurance plan is not compatible with MedBox. Unfortunately, this means we cannot proceed with your sign-up at this time. We will keep your details on file and notify you if the situation changes in the future.';
        } else {
            if (customDescriptionWebhookSuccess) {
                return customDescriptionWebhookSuccess;
            }
            return 'I’m sorry, but it looks like we’ve identified an issue with your insurance and are unable to resume the sign up process at this time. Please check your email for important information.';
        }
    }, [hasError, customDescriptionWebhookFailure, customDescriptionWebhookSuccess]);

    const title = hasError ? 'Insurance Not Compatible' : 'Check your email';

    const handleClose = () => {
        setSuccess(false);
        onClose ? onClose() : _.noop();
    };

    return (
        <Modal dataTestId="unserviceable" open={!!conflict && success} contentClassName="overflow-hidden" onClose={handleClose} hideCloseIcon={true}>
            <div data-testid="unserviceable-container" className="flex flex-col items-center justify-center">
                <img width={56} className="mb-2" src={WarningRed} alt="warning" />

                {loading ? (
                    <>
                        <div className="mx-auto mb-5 h-[1.75rem] w-[50%] rounded bg-gray-300"></div>
                        <div className="mx-auto mb-[2.2rem] h-[3rem] w-[80%] rounded bg-gray-300"></div>
                    </>
                ) : (
                    <>
                        <h3 className="mb-5 text-center text-[1.75rem]" data-testid="unserviceable-title">{title}</h3>
                        <div className="mb-[2.2rem] max-w-[100%] text-center text-base" data-testid="unserviceable-description">{description}</div>
                    </>
                )}
                <div className="flex w-full flex-col">
                    <Button
                        disabled={loading}
                        className="insurance__add_insurance_btn"
                        type="submit"
                        sx={{ padding: '1rem', width: '100%', marginBottom: '1rem', fontSize: '1.125rem' }}
                        onClick={onContinue}
                        fixedEndIcon={<i className="icon-thin-arrow-right text-base" />}
                        testId="button-insurance-unserviceable-proceed"
                    >
                        <span>{hasInsurance ? 'Continue' : 'Proceed without insurance'}</span>
                    </Button>
                    <Button
                        disabled={loading}
                        className="insurance__add_another_btn"
                        sx={{ padding: '1rem', width: '100%', fontSize: '1.125rem' }}
                        variant="outlined"
                        onClick={onAdd}
                        testId="button-insurance-unserviceable-add-another"
                    >
                        <span>Add another insurance</span>
                    </Button>
                    {!hasError && !loading && (
                        <Button
                            className="insurance__resend_email_btn"
                            sx={{ padding: '1rem', width: '100%', fontSize: '1.125rem' }}
                            variant="text"
                            onClick={sendEmail}
                            testId="button-insurance-unserviceable-resend-email"
                        >
                            <span>Resend email</span>
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default InsuranceUnserviceableModal;
